import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Maximize,
  Minimize,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
} from 'lucide-react';

export type CoverMediaKind = 'image' | 'video' | null;
export type LearnFrame = '16/9' | '9/16';

type Props = {
  src: string | null | undefined;
  kind?: CoverMediaKind;
  alt?: string;
  className?: string;
  mediaClassName?: string;
  /** Playable player (detail). False on cards — no video download, no play. */
  playable?: boolean;
  /** Still shown on cards and before the video plays. */
  poster?: string | null;
  /**
   * Learn covers: pick a fixed 16:9 or 9/16 frame from media orientation
   * and fit the full image/video inside (no crop).
   */
  learnFrame?: boolean;
};

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

function inferKind(src: string | null | undefined, kind?: CoverMediaKind): CoverMediaKind {
  if (kind) return kind;
  if (!src) return null;
  const lower = src.toLowerCase();
  if (/\.(mp4|webm|mov)(\?|$)/.test(lower) || lower.includes('video/')) return 'video';
  return 'image';
}

function frameFromSize(width: number, height: number): LearnFrame {
  return height > width ? '9/16' : '16/9';
}

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function SmartCoverMedia({
  src,
  kind,
  alt = '',
  className = '',
  mediaClassName,
  playable = true,
  learnFrame = false,
  poster,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [frame, setFrame] = useState<LearnFrame>('16/9');
  const resolved = inferKind(src, kind);

  useEffect(() => {
    const onFs = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  if (!src) return null;

  const fitClass = learnFrame
    ? 'w-full h-full object-contain'
    : (mediaClassName ?? 'w-full h-full object-cover');

  const shellClass = learnFrame
    ? `relative w-full overflow-hidden ${
        fullscreen
          ? 'bg-black w-screen h-screen max-h-none aspect-auto'
          : `${frame === '9/16' ? 'aspect-[9/16]' : 'aspect-[16/9]'} bg-teal-soft`
      } ${fullscreen ? '' : className}`
    : `relative overflow-hidden ${fullscreen ? 'bg-black w-screen h-screen max-h-none' : className}`;

  const onDims = (width: number, height: number) => {
    if (!learnFrame || !width || !height) return;
    setFrame(frameFromSize(width, height));
  };

  if (resolved !== 'video') {
    return (
      <div className={shellClass}>
        <img
          src={src}
          alt={alt}
          className={fitClass}
          loading="lazy"
          decoding="async"
          onLoad={(e) => {
            const img = e.currentTarget;
            onDims(img.naturalWidth, img.naturalHeight);
          }}
        />
      </div>
    );
  }

  if (!playable) {
    return (
      <div className={`${shellClass} pointer-events-none`} aria-hidden>
        {poster ? (
          <img
            src={poster}
            alt=""
            className={fitClass}
            loading="lazy"
            decoding="async"
            onLoad={(e) => {
              const img = e.currentTarget;
              onDims(img.naturalWidth, img.naturalHeight);
            }}
          />
        ) : null}
        <div className={`absolute inset-0 flex items-center justify-center ${poster ? 'bg-black/25' : 'bg-ink/80'}`}>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/95 text-ink shadow-sm">
            <Play className="w-5 h-5 ml-0.5" />
          </span>
        </div>
      </div>
    );
  }

  const el = () => videoRef.current;

  const togglePlay = () => {
    const v = el();
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
  };

  const skip = (delta: number) => {
    const v = el();
    if (!v) return;
    const apply = () => {
      const dur = v.duration;
      const cap = Number.isFinite(dur) && dur > 0 ? dur : Number.MAX_SAFE_INTEGER;
      const next = Math.min(cap, Math.max(0, v.currentTime + delta));
      v.currentTime = next;
      setCurrent(next);
    };
    if (v.readyState >= 1) {
      apply();
      return;
    }
    v.addEventListener('loadedmetadata', apply, { once: true });
    v.preload = 'metadata';
    v.load();
  };

  const toggleMute = () => {
    const v = el();
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const cycleRate = () => {
    const v = el();
    if (!v) return;
    const i = RATES.indexOf(rate);
    const next = RATES[(i + 1) % RATES.length] ?? 1;
    v.playbackRate = next;
    setRate(next);
  };

  const seek = (value: number) => {
    const v = el();
    if (!v) return;
    v.currentTime = value;
    setCurrent(value);
  };

  const toggleFullscreen = async () => {
    const box = wrapRef.current;
    const v = el();
    if (!box) return;
    const wk = v as HTMLVideoElement & { webkitEnterFullscreen?: () => void };
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      if (box.requestFullscreen) {
        await box.requestFullscreen();
        return;
      }
      wk.webkitEnterFullscreen?.();
    } catch {
      wk.webkitEnterFullscreen?.();
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`relative group/media bg-black fullscreen:bg-black fullscreen:w-screen fullscreen:h-screen fullscreen:max-h-none fullscreen:aspect-auto ${shellClass}`}
    >
      <video
        ref={videoRef}
        src={src}
        className={`${fitClass} bg-black fullscreen:h-full fullscreen:w-full fullscreen:object-contain`}
        playsInline
        poster={poster || undefined}
        preload="metadata"
        controls={false}
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          v.muted = false;
          v.volume = 1;
          setDuration(v.duration || 0);
          onDims(v.videoWidth, v.videoHeight);
        }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onPlay={(e) => {
          e.currentTarget.muted = false;
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlay();
        }}
      />
      {!playing && (
        <button
          type="button"
          aria-label="Play"
          className="absolute inset-x-0 top-0 bottom-16 z-[1] flex items-center justify-center bg-black/20"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            togglePlay();
          }}
        >
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-ink shadow-sm">
            <Play className="w-6 h-6 ml-0.5" />
          </span>
        </button>
      )}
      <div className="absolute inset-x-0 bottom-0 z-[2] p-2 bg-gradient-to-t from-black/70 to-transparent opacity-100 sm:opacity-0 sm:group-hover/media:opacity-100 transition-opacity">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={current}
          aria-label="Seek"
          className="w-full h-1 mb-2 accent-sand cursor-pointer"
          onClick={e => e.stopPropagation()}
          onChange={(e) => seek(Number(e.target.value))}
        />
        <div className="flex items-center gap-1.5 text-white">
          <ControlBtn label="Back 10 seconds" onClick={() => skip(-10)}>
            <RotateCcw className="w-4 h-4" />
          </ControlBtn>
          <ControlBtn label={playing ? 'Pause' : 'Play'} onClick={togglePlay} large>
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </ControlBtn>
          <ControlBtn label="Forward 10 seconds" onClick={() => skip(10)}>
            <RotateCw className="w-4 h-4" />
          </ControlBtn>
          <span className="text-[11px] tabular-nums text-white/90 px-1 shrink-0">
            {formatTime(current)} / {formatTime(duration)}
          </span>
          <div className="flex-1" />
          <ControlBtn label={muted ? 'Unmute' : 'Mute'} onClick={toggleMute}>
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </ControlBtn>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              cycleRate();
            }}
            className="min-w-[2.5rem] h-9 px-1.5 rounded-full bg-white/90 text-ink text-[11px] font-semibold touch-manipulation"
            aria-label="Playback speed"
          >
            {rate === 1 ? '1×' : `${rate}×`}
          </button>
          <ControlBtn label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} onClick={() => void toggleFullscreen()}>
            {fullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </ControlBtn>
        </div>
      </div>
    </div>
  );
}

function ControlBtn({
  label,
  onClick,
  large,
  children,
}: {
  label: string;
  onClick: () => void;
  large?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={`inline-flex items-center justify-center rounded-full bg-white/90 text-ink shadow-sm touch-manipulation ${
        large ? 'w-11 h-11' : 'w-9 h-9'
      }`}
    >
      {children}
    </button>
  );
}
